import React, { useEffect, useState, useRef, useCallback } from "react";

import useOutsideClick from "../../hooks/useOutsideClick";
import { useMessage } from "../../context/MessageContext";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import { getApi } from "../../apis/api";
import { XCrossIcon } from "../../assets";

import "../../assets/scss/abstracts/_form.scss";

interface MutliDropdownType {
  columns: ColumnTypes[];
  captionTitles?: string;
  data: any[];
  setData: (data: any) => void;
  rowData: any;
  setRowData: (row: any) => void;
  displayColumn: string;
  agentId?: string;
  type?: string;
  apiUrl: string;
  limit?: number;
  setAgentRowData?: (data: any) => void;
  agentFind?: boolean;
  disable?: boolean;
  enableRemove?: boolean;
  labelTitle?: string;
  mandatory?: boolean;
  checkInputChange?: () => void;
  parentCustomerId?: string;
  skipApiCallWhenNoParent?: boolean;
  extendedUrl?: Record<string, string>;
  status?: number;
  extraParentId?: {
    key: string;
    id: string;
  };
  from?: string;

  // Formik bridge (optional)
  setFieldValue?: (field: string, value: any) => void;
  extraFields?: string[];

  /** Free-text fallback controls */
  useFreeTextOnNoSelect?: boolean;
  overwriteWithFreeText?: boolean;

  /** id plumbing (optional) */
  idFieldName?: string;
  onSelectId?: (id: string | null) => void;
  clearIdOnManual?: boolean;

  /** Optional override for the query param name used for search */
  searchParamKey?: string; // default auto: 'description' for customeritemlist, else 'addressStr'

  /** Customer ID to filter results by selected customer */
  customerId?: string;
  /**
   * Identifies this control to the mandatory-field validation, which locates fields by
   * `data-field` and focuses the caption when a required value is missing.
   */
  fieldKey?: string;
  /** Validation message rendered beneath the control. */
  error?: string;

}

interface ColumnTypes {
  key: string;
  title: string;
}

const ADDRESS_FIELD_KEYS = [
  "address",
  "aptSuit",
  "zipCode",
  "city",
  "state",
  "country",
  "businessLocation",
  "contactPerson",
  "phoneNumber",
  "countryCallingCode",
  "phoneExtension",
  "contactEmail",
  "operationHrsFrom",
  "operationHrsTo",
  "pickupDockOpenTime",
  "pickupDockCloseTime",
  "dropDockOpenTime",
  "dropDockCloseTime",
  "latitude",
  "longitude",
];

const AddressDropDown: React.FC<MutliDropdownType> = ({
  columns,
  captionTitles = "Select",
  data,
  setData,
  rowData,
  setRowData,
  displayColumn,
  agentId,
  type,
  apiUrl,
  limit = 15,
  setAgentRowData,
  agentFind = false,
  disable,
  enableRemove,
  labelTitle,
  mandatory,
  checkInputChange,
  parentCustomerId,
  skipApiCallWhenNoParent,
  extendedUrl,
  status = 1,
  extraParentId,
  from,
  setFieldValue,
  extraFields,
  useFreeTextOnNoSelect = true,
  overwriteWithFreeText = false,
  idFieldName,
  onSelectId,
  clearIdOnManual = true,
  searchParamKey,
  customerId,
  fieldKey,
  error,
}) => {
  const { setMessage } = useMessage();
  const [isSelected, setIsSelected] = useState<number | null>(null);
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [isDivFocused, setIsDivFocused] = useState(false);
  const isFetching = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // keep a stable reference to setData (parents may pass a new function every render)
  const setDataRef = useRef(setData);
  useEffect(() => {
    setDataRef.current = setData;
  }, [setData]);

  // Prevent stale response flicker
  const reqSeq = useRef(0);

  const MIN_CHARS = 1;
  const keywordOk = keyword.trim().length >= MIN_CHARS;

  const isCustomerItemListEndpoint = apiUrl?.includes(
    "/customer/saveddetails/customeritemlist"
  );
  const finalSearchParam =
    searchParamKey ?? (isCustomerItemListEndpoint ? "description" : "addressStr");

  const buildFreeTextRow = (text: string) => {
    const trimmed = text.trim();
    return {
      id: undefined, // manual entries should not carry a persisted id
      [displayColumn]: trimmed,
      address: trimmed,
      isManual: true,
    };
  };

  const mirrorToFormik = (obj: any) => {
    if (setFieldValue) {
      for (const k of ADDRESS_FIELD_KEYS) {
        if (obj[k] !== undefined) {
          const v =
            k === "contactEmail" && Array.isArray(obj[k])
              ? (obj[k][0] ?? "")
              : obj[k];
          setFieldValue(k, v);
        }
      }
      if (idFieldName) {
        const valueForId = obj?.isManual && clearIdOnManual ? "" : (obj?.id ?? "");
        setFieldValue(idFieldName, valueForId);
      }
    }
    if (onSelectId) {
      const idForCb =
        obj?.isManual && clearIdOnManual ? null : (obj?.id ?? null);
      onSelectId(idForCb);
    }
  };

  // NEW: helper to clear everything to blank (for Backspace-to-empty flow)
  const clearToBlank = () => {
    // Clear local
    setRowData(null);
    setIsSelected(null);

    // Clear mirrored formik values
    if (setFieldValue) {
      for (const k of ADDRESS_FIELD_KEYS) {
        setFieldValue(k, "");
      }
      if (idFieldName) setFieldValue(idFieldName, "");
    }

    // Notify parent
    if (onSelectId) onSelectId(null);

    // Change marker
    checkInputChange && checkInputChange();
  };

  /**
   * If user edited text after selecting an option, persist that edit,
   * including clearing everything when input is blank.
   */
  const commitFreeTextIfNeeded = () => {
    const typed = keyword.trim();
    const selectedText = rowData?.[displayColumn] ?? "";
    const hasSelection = !!selectedText || !!rowData;

    // If input is blank and something was selected/typed before -> clear
    if (!typed) {
      if (hasSelection) {
        clearToBlank();
      }
      return;
    }

    // If there was a selection and user changed text -> persist manual
    if (hasSelection && typed !== selectedText) {
      const manual = buildFreeTextRow(typed);
      setRowData(manual);
      mirrorToFormik(manual); // clears id for manual
      checkInputChange && checkInputChange();
      return;
    }

    // Previous behavior for no selection
    if (!hasSelection && useFreeTextOnNoSelect) {
      const manual = buildFreeTextRow(typed);
      setRowData(manual);
      mirrorToFormik(manual); // clears id for manual
      checkInputChange && checkInputChange();
    }
  };

  const handleOpened = () => {
    if (disable) return;
    if (isOpened) {
      // Closing: commit manual if user edited
      commitFreeTextIfNeeded();
    }
    setIsOpened((prev) => {
      const next = !prev;
      if (next) {
        setIsSearchVisible(true);
        setPage(1);
        setHasMore(true);
        setIsSelected(null);
        setDataRef.current([]); // clear stale items
        setKeyword(rowData?.[displayColumn] ?? "");
      } else {
        setIsSearchVisible(false);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isSearchVisible && inputRef.current) {
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
      inputRef.current.focus();
    }
  }, [isSearchVisible]);

  const outsideClickRef = useOutsideClick<HTMLDivElement>(() => {
    if (isOpened) {
      commitFreeTextIfNeeded();
    }
    setIsOpened(false);
    setIsSearchVisible(false);
    setIsDivFocused(false);
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpened) return;

    switch (e.key) {
      case "Tab":
        // Commit any free text, close dropdown, and let focus move to next form element
        commitFreeTextIfNeeded();
        setIsOpened(false);
        setIsSearchVisible(false);
        setKeyword("");
        setIsSelected(null);
        setDataRef.current([]);
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!data?.length) return;
        if (isSelected === null || isSelected === data.length - 1) {
          setIsSelected(0);
        } else {
          setIsSelected((prev) => (prev! + 1) % data.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!data?.length) return;
        if (isSelected === null || isSelected === 0) {
          setIsSelected(data.length - 1);
        } else {
          setIsSelected((prev) => (prev! - 1 + data.length) % data.length);
        }
        break;
      case "Enter": {
        e.preventDefault();
        const typed = keyword.trim();
        const selectedText = rowData?.[displayColumn] ?? "";

        // If an item is highlighted, select it
        if (isSelected !== null && data?.[isSelected]) {
          const selected = data[isSelected];
          setRowData(selected);
          mirrorToFormik(selected); // guarantees id gets propagated
          setIsSelected(null);
          checkInputChange && checkInputChange();
        } else if (keywordOk && data.length > 0) {
          // Try exact case-insensitive match first (same as SingleDropdown)
          const exactMatch = data.find(
            (item) => item[displayColumn]?.toLowerCase() === typed.toLowerCase()
          );
          if (exactMatch) {
            setRowData(exactMatch);
            mirrorToFormik(exactMatch);
            setIsSelected(null);
            checkInputChange && checkInputChange();
          } else if (typed !== selectedText) {
            // No exact match — fall back to free text (AddressDropDown-specific)
            const manual = buildFreeTextRow(keyword);
            setRowData(manual);
            mirrorToFormik(manual); // clears id
            checkInputChange && checkInputChange();
          }
        } else if (keywordOk) {
          // Has keyword but no data loaded yet — persist as free text
          if (typed !== selectedText) {
            const manual = buildFreeTextRow(keyword);
            setRowData(manual);
            mirrorToFormik(manual);
            checkInputChange && checkInputChange();
          }
        } else {
          // If keyword is empty on Enter and something was selected -> clear
          if (!typed && (rowData || selectedText)) {
            clearToBlank();
          }
        }
        setIsOpened(false);
        setIsSearchVisible(false);
        setKeyword("");
        setDataRef.current([]);
        break;
      }
      default:
        break;
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setKeyword(v);
    setPage(1);
    setHasMore(true);
    setIsSelected(null); // Reset highlighted item when typing

    const trimmed = v.trim();

    if (trimmed.length < MIN_CHARS) {
      // Stop showing suggestions
      setDataRef.current([]);
      setHasMore(false);

      // If user has backspaced to empty, clear selection + form values
      if (!trimmed) {
        clearToBlank();
      }
      return;
    }
  };

  const handleRemoveRowData = () => {
    clearToBlank();
    setIsOpened(false);
    setIsSearchVisible(false);
  };

  // Auto-scroll to highlighted item during keyboard navigation
  useEffect(() => {
    if (isSelected !== null && isOpened && dropdownRef.current) {
      const container = dropdownRef.current;
      const highlightedItem = container.querySelector(".highlighted") as HTMLElement;
      if (highlightedItem) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const itemTop = highlightedItem.offsetTop;
        const itemBottom = itemTop + highlightedItem.clientHeight;

        if (itemTop < containerTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerBottom) {
          container.scrollTop = itemBottom - container.clientHeight;
        }
      }
    }
  }, [isSelected, isOpened]);

  const getExtendedUrlParams = () => {
    if (!extendedUrl) return "";
    return Object.entries(extendedUrl)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join("&");
  };

  const fetchFilteredData = useCallback(
    async (reset: boolean = false) => {
      if (!keywordOk) return;
      if (
        isFetching.current ||
        !apiUrl ||
        (skipApiCallWhenNoParent && !parentCustomerId)
      ) {
        return;
      }

      isFetching.current = true;
      setIsLoading(true);
      const myReq = ++reqSeq.current;

      try {
        let url = `${apiUrl}?limit=${limit}&page=${reset ? 1 : page}`;

        if (keyword) url += `&${finalSearchParam}=${encodeURIComponent(keyword)}`;
        if (agentId) url += `&agentId=${agentId}`;
        if (type) url += `&type=${type}`;
        if (parentCustomerId) url += `&parentCustomerId=${parentCustomerId}`;
        if (customerId) url += `&customerId=${customerId}`;
        if (extraParentId?.id) url += `&${extraParentId.key}=${extraParentId.id}`;
        if ([1, 0].includes(Number(status))) url += `&status=${status}`;

        const ext = getExtendedUrlParams();
        if (ext) url += `&${ext}`;

        const response = await getApi(url);

        // If a newer request started, ignore this one
        if (myReq !== reqSeq.current) return;

        if (response.success) {
          const userData: any[] = Array.isArray(response.data)
            ? response.data
            : [];

          // Normalize items so displayColumn works everywhere
          const mappedData = userData.map((item: any) => {
            const normalized: any = { ...item };
            if (isCustomerItemListEndpoint) {
              // expose 'address' for display (parent reads full object anyway)
              normalized.address = item.description ?? "";
            }
            // Ensure we carry a stable id for parent + callbacks
            normalized.id =
              item.id ??
              item.customerId ??
              normalized[displayColumn] ??
              `${Math.random()}`;
            return normalized;
          });

          if (agentFind) {
            const agent = userData.find((it: any) => it.id === agentId);
            setAgentRowData && setAgentRowData(agent);
          }

          setDataRef.current((prev: any[]) =>
            reset ? mappedData : [...(prev || []), ...mappedData]
          );

          if (response?.pagination?.totalPages) {
            const checkPage = reset ? 1 : page;
            setHasMore(checkPage < response?.pagination?.totalPages);
          } else {
            setHasMore(userData.length >= limit);
          }
        } else {
          setMessage(response.error?.message || GENERAL_ERROR_MESSAGE, "error");
        }
      } catch (error) {
        console.log(GENERAL_ERROR_MESSAGE, "error");
      } finally {
        if (myReq === reqSeq.current) {
          setIsLoading(false);
        }
        isFetching.current = false;
      }
    },
    [
      apiUrl,
      keyword,
      page,
      limit,
      agentId,
      setAgentRowData,
      parentCustomerId,
      customerId,
      extraParentId?.id,
      status,
      skipApiCallWhenNoParent,
      keywordOk,
      type,
      setMessage,
      displayColumn,
      finalSearchParam,
      isCustomerItemListEndpoint,
    ]
  );

  // Debounced search while open
  useEffect(() => {
    if (!isOpened) return;
    if (keywordOk) {
      const t = setTimeout(() => {
        fetchFilteredData(true);
      }, 500);
      return () => clearTimeout(t);
    } else {
      setDataRef.current([]);
      setHasMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, keyword, keywordOk, isOpened]);

  // Infinite scroll while searching
  useEffect(() => {
    const handleScroll = () => {
      if (!dropdownRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        !isLoading &&
        hasMore &&
        keywordOk
      ) {
        setPage((prev) => prev + 1);
      }
    };
    const currentRef = dropdownRef.current;
    if (currentRef) currentRef.addEventListener("scroll", handleScroll);
    return () => {
      if (currentRef) currentRef.removeEventListener("scroll", handleScroll);
    };
  }, [isLoading, hasMore, keywordOk]);

  // page change fetch — only while searching
  useEffect(() => {
    if (page > 1 && keywordOk && isOpened) {
      fetchFilteredData();
    }
  }, [page, keywordOk, isOpened, fetchFilteredData]);

  // parent change
  useEffect(() => {
    if (parentCustomerId || extraParentId) {
      handleRemoveRowData();
    }
    if (keywordOk && isOpened) {
      setPage(1);
      fetchFilteredData(true);
    } else {
      setDataRef.current([]);
    }
  }, [parentCustomerId, extraParentId?.id, keywordOk, isOpened, fetchFilteredData]);

  // Auto-highlight the already-selected item when dropdown opens (like SingleDropdown)
  const hasAutoHighlightedRef = useRef(false);
  useEffect(() => {
    if (!isOpened) {
      hasAutoHighlightedRef.current = false;
      return;
    }
    if (hasAutoHighlightedRef.current) return;
    if (rowData && data.length > 0) {
      const selectedIndex = data.findIndex((item) =>
        rowData.id != null
          ? String(item.id) === String(rowData.id)
          : item[displayColumn]?.toLowerCase() === rowData[displayColumn]?.toLowerCase()
      );
      if (selectedIndex !== -1) {
        setIsSelected(selectedIndex);
        hasAutoHighlightedRef.current = true;
      }
    }
  }, [isOpened, data, rowData, keyword]);

  const handleFocus = () => setIsDivFocused(true);
  const handleBlur = () => setIsDivFocused(false);

  const selectAndClose = (selected: any) => {
    setRowData(selected);
    mirrorToFormik(selected); // send id through callbacks too
    setIsSelected(null);
    checkInputChange && checkInputChange();
    setIsOpened(false);
    setIsSearchVisible(false);
    setKeyword("");
    setDataRef.current([]);
  };

  return (
    <>
      <style>{`
        .customSelect__dropdown { z-index: 9999; position: absolute; }
      `}</style>

      <div className="form__Field">
        {labelTitle && (
          <label className="form__label">
            {labelTitle} {mandatory && <span className="mandatory-mark">*</span>}
          </label>
        )}
        <div
          tabIndex={0}
          onFocus={handleFocus}
          onBlur={handleBlur}
          data-field={fieldKey}
          className={`customSelect ${isOpened ? "open" : ""} ${error ? "customSelect--error" : ""}`}
          // style={isDivFocused && !disable ? { border: "solid 1px" } : { border: "0px" }}
          ref={outsideClickRef}
        >
          {isSearchVisible ? (
            <input
              ref={inputRef}
              type="text"
              placeholder="Search..."
              value={keyword}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              className="form__input form__input--search form__input--sm"
              autoFocus
            />
          ) : (
            <div
              className={`customSelect__caption ${disable ? "customSelect__caption--disabled" : ""
                }`}
              onClick={handleOpened}
            >
              {rowData?.[displayColumn] ? (
                rowData?.[displayColumn]
              ) : (
                <span className="customSelect__caption__placeholder">
                  {captionTitles}
                </span>
              )}

              {rowData?.[displayColumn] && enableRemove && !disable && (
                <span
                  style={{
                    background: "#fff",
                    position: "absolute",
                    right: "8px",
                    top: "10px",
                  }}
                  onClick={handleRemoveRowData}
                >
                  <XCrossIcon />
                </span>
              )}
            </div>
          )}

          {/* Render dropdown only when open AND we have enough chars */}
          {isOpened && keywordOk && (
            <div
              className={`customSelect__dropdown ${columns.length === 3 ? "customSelect__dropdown--width450" : ""
                }`}
              ref={dropdownRef}
            >
              <div className="customSelect__dropdown__body">
                {isLoading && page === 1 ? (
                  <div className="customSelect__dropdown__noitem">
                    <div className="col">Loading...</div>
                  </div>
                ) : !data || data.length === 0 ? (
                  <div className="customSelect__dropdown__noitem">
                    <div className="col">No data found.</div>
                  </div>
                ) : (
                  data.map((item, index) => (
                    <div
                      className={`customSelect__dropdown__item ${item?.id === rowData?.id ? "selected" : ""} ${index === isSelected ? "highlighted" : ""}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        selectAndClose(item);
                      }}
                      key={item.id ?? index}
                    >
                      <div className="col">{item[displayColumn]}</div>
                    </div>
                  ))
                )}
                {isLoading && page > 1 && (
                  <div className="customSelect__dropdown__noitem">
                    <div className="col">Loading more...</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {error && <span className="form__error">{error}</span>}
      </div>
    </>
  );
};

export default AddressDropDown;
